/**
 * The design studio carrying an ad's thread (docs/SPEC.md §6.4; P28-T14).
 * Opened from an ad's cast panel, every page of the design says what it is
 * for — "Making the {thing} for {ad title}" — with **Back to the ad** (the
 * design stays as it is) and, once the design has something built, **Use in
 * {ad title}**: the design goes to Objects, made for the ad, and replaces the
 * stand-in; the studio then opens the ad's page, which says what happens.
 * When the ad is gone (deleted) there is no banner.
 */
import { Link, useNavigate } from "react-router-dom";
import { type FromStudio, useUseInAd } from "../api/models";
import type { ModelDetail } from "../api/types";
import { CAST, sayCast } from "../copy/cast";
import { sentenceFor } from "../copy/errors";
import { Button } from "../ui";

export interface StudioThreadBannerProps {
  /** The ad the design is made for; null without a thread. */
  adId: string | null | undefined;
  /** Its title; null when the ad is gone. */
  adTitle: string | null | undefined;
  /** What is being made ("sofa"). */
  thing: string | null | undefined;
  /** The design, once it exists: **Use in {ad}** acts on it. */
  design?: ModelDetail;
}

export function StudioThreadBanner({
  adId,
  adTitle,
  thing,
  design,
}: StudioThreadBannerProps) {
  const navigate = useNavigate();
  const use = useUseInAd(design?.id ?? "");
  if (!adId || !adTitle) return null;
  const ad = adTitle;
  // something is built and the design is not in the middle of a step
  const ready = !!design?.current && design.state !== "working";
  return (
    <section
      className="mb-4 flex flex-col gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4 md:flex-row md:items-center md:justify-between"
      aria-label={sayCast(CAST.banner, { thing: thing ?? "object", ad })}
      data-testid="studio-thread"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <p className="m-0 break-words text-base font-semibold text-slate-900">
          {sayCast(CAST.banner, { thing: thing ?? "object", ad })}
        </p>
        <p className="m-0 text-sm text-slate-700">
          {sayCast(design ? CAST.bannerHelp : CAST.bannerHelpNew, { ad })}
        </p>
        {design && !ready ? (
          <p className="m-0 text-xs text-slate-600">{CAST.useWaits}</p>
        ) : null}
        {use.error ? (
          <p role="alert" className="m-0 text-sm text-slate-800">
            {sentenceFor(use.error, "design")}
          </p>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2 max-sm:[&_.tb-btn]:min-h-[44px]">
        <Button
          as={Link}
          to={`/ads/${encodeURIComponent(adId)}`}
          variant="secondary"
          size="sm"
        >
          {CAST.backToAd}
        </Button>
        {design ? (
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={!ready || use.isPending}
            aria-busy={use.isPending}
            className="disabled:cursor-not-allowed disabled:opacity-50"
            onClick={() =>
              use.mutate(undefined, {
                onSuccess: (answer) => {
                  const state: FromStudio = { usedInAd: answer };
                  navigate(`/ads/${encodeURIComponent(answer.ad_id)}`, {
                    state,
                  });
                },
              })
            }
          >
            {use.isPending ? CAST.using : sayCast(CAST.useIn, { ad })}
          </Button>
        ) : null}
      </div>
    </section>
  );
}
